import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, XCircle, Question, Clock } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-72 — Admin panel showing pending buyer criteria-change requests
 * for a single client org, embedded in ManageBuyer.jsx.
 *
 * Renders nothing when there are zero pending rows (keeps the manage
 * page clean when there's no work to do). For each pending row it
 * shows a per-field diff of `current_criteria_snapshot` vs
 * `proposed_criteria`, plus Approve / Reject buttons. Approve auto-
 * applies the proposal via the backend (which re-runs the same
 * `validate_intake_criteria` + state-subset guard the admin PATCH uses).
 */

// Human labels for every criterion the backend recognises. Fields not
// listed here fall back to a title-cased key. Keeping this local to the
// panel avoids a shared-vocab refactor of ManageBuyer.
const FIELD_LABELS = {
  accepted_states: "Accepted states",
  accepted_zips: "Accepted ZIPs",
  accepted_counties: "Accepted counties",
  case_types: "Case types",
  max_incident_age_days: "Max incident age (days)",
  allow_ongoing_treatment_older_cases: "Allow ongoing treatment on older cases",
  injury_required: "Injury required",
  treatment_required: "Treatment required",
  police_report_required: "Police report required",
  claimant_insured: "Claimant insured",
  at_fault_party_insured: "At-fault party insured",
  attorney_policy: "Attorney policy",
  hit_and_run: "Hit and run",
  uninsured_motorist: "Uninsured motorist",
  accepted_fault_levels: "Accepted fault levels",
  accepted_lead_types: "Accepted lead types",
  treatment_within_days: "Treatment within (days)",
  min_age: "Min consumer age",
  max_age: "Max consumer age",
};

function label(key) {
  return FIELD_LABELS[key]
    || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtValue(v) {
  if (v == null) return <span className="text-zinc-400">—</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="text-zinc-400">(empty)</span>;
    return <span className="font-mono text-[11px]">{v.join(", ")}</span>;
  }
  if (typeof v === "boolean") return v ? "yes" : "no";
  return <span className="font-mono text-[11px]">{String(v)}</span>;
}

function diffFields(current, proposed) {
  const keys = new Set([
    ...Object.keys(current || {}),
    ...Object.keys(proposed || {}),
  ]);
  const rows = [];
  keys.forEach((k) => {
    const a = (current || {})[k];
    const b = (proposed || {})[k];
    if (JSON.stringify(a) !== JSON.stringify(b)) {
      rows.push({ key: k, current: a, proposed: b });
    }
  });
  return rows.sort((x, y) => x.key.localeCompare(y.key));
}

function StatusChip({ status }) {
  const style = {
    pending: "border-amber-800 bg-amber-100 text-amber-900",
    approved: "border-emerald-800 bg-emerald-100 text-emerald-900",
    rejected: "border-rose-800 bg-rose-100 text-rose-900",
  }[status] || "border-zinc-800 bg-zinc-100 text-zinc-900";
  return (
    <span
      className={`px-1.5 py-0.5 text-[9px] uppercase tracking-widest border ${style}`}
      data-testid={`req-status-${status}`}
    >
      {status}
    </span>
  );
}

export default function BuyerCriteriaRequestsPanel({ clientOrgId, onApproved }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [showResolved, setShowResolved] = useState(false);

  const load = useCallback(async () => {
    if (!clientOrgId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/buyer-criteria-requests?client_org_id=${clientOrgId}`,
      );
      setRows(data.requests || []);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Failed to load buyer requests",
      );
    } finally {
      setLoading(false);
    }
  }, [clientOrgId]);

  useEffect(() => { load(); }, [load]);

  const pending = useMemo(
    () => rows.filter((r) => r.status === "pending"),
    [rows],
  );
  const resolved = useMemo(
    () => rows.filter((r) => r.status !== "pending"),
    [rows],
  );

  const approve = async (row) => {
    setBusyId(row.id);
    try {
      await api.post(
        `/admin/buyer-criteria-requests/${row.id}/approve`,
        { review_notes: null },
      );
      toast.success("Request approved and applied to buyer criteria.");
      await load();
      onApproved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : detail?.message || JSON.stringify(detail) || "Approve failed";
      toast.error(msg);
    } finally {
      setBusyId(null);
    }
  };

  const startReject = (row) => {
    setRejectingId(row.id);
    setRejectNotes("");
  };

  const confirmReject = async () => {
    if (!rejectNotes.trim()) {
      toast.error("Please explain why you're rejecting this request.");
      return;
    }
    setBusyId(rejectingId);
    try {
      await api.post(
        `/admin/buyer-criteria-requests/${rejectingId}/reject`,
        { review_notes: rejectNotes.trim() },
      );
      toast.success("Request rejected.");
      setRejectingId(null);
      setRejectNotes("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusyId(null);
    }
  };

  // Hide the whole panel when there's nothing pending AND the admin
  // hasn't opted to see resolved rows — keeps the manage page tidy.
  if (loading && rows.length === 0) return null;
  if (pending.length === 0 && !showResolved) {
    if (resolved.length === 0) return null;
    return (
      <div
        className="brut-card p-3 text-[11px] text-zinc-600 flex items-center justify-between"
        data-testid="criteria-requests-empty"
      >
        <div className="inline-flex items-center gap-2">
          <Question size={12} /> No pending criteria requests for this buyer.
        </div>
        <button
          onClick={() => setShowResolved(true)}
          data-testid="show-resolved-requests"
          className="border border-black px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white"
        >
          Show {resolved.length} resolved
        </button>
      </div>
    );
  }

  return (
    <div className="brut-card" data-testid="criteria-requests-panel">
      <div className="p-3 border-b border-black flex items-center gap-2 flex-wrap">
        <Clock size={14} weight="bold" />
        <div className="font-chivo font-black uppercase tracking-tighter">
          Buyer criteria requests
        </div>
        <span className="label-mono ml-auto">
          {pending.length} pending · {resolved.length} resolved
        </span>
      </div>

      {pending.map((row) => {
        const diffs = diffFields(row.current_criteria_snapshot, row.proposed_criteria);
        const isRejecting = rejectingId === row.id;
        return (
          <div
            key={row.id}
            className="p-3 border-b border-black last:border-b-0"
            data-testid={`criteria-request-${row.id}`}
          >
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <StatusChip status={row.status} />
              <div className="text-xs">
                <b>{row.requester_name}</b>
                <span className="text-zinc-500 font-mono ml-1">
                  &lt;{row.requester_email}&gt;
                </span>
              </div>
              <div className="ml-auto text-[10px] font-mono text-zinc-500">
                {new Date(row.created_at).toLocaleString()}
              </div>
            </div>

            {row.notes && (
              <div className="mb-3 p-2 border-l-4 border-amber-500 bg-amber-50 text-xs whitespace-pre-wrap">
                {row.notes}
              </div>
            )}

            {diffs.length === 0 ? (
              <div className="text-[11px] text-zinc-500 italic mb-3">
                No effective changes vs current criteria.
              </div>
            ) : (
              <div className="border border-black mb-3">
                <div className="grid grid-cols-[1fr_1fr_1fr] text-[10px] uppercase tracking-widest bg-zinc-100 border-b border-black">
                  <div className="p-2">Field</div>
                  <div className="p-2 border-l border-black">Current</div>
                  <div className="p-2 border-l border-black">Proposed</div>
                </div>
                {diffs.map((d) => (
                  <div
                    key={d.key}
                    className="grid grid-cols-[1fr_1fr_1fr] text-xs border-b border-zinc-200 last:border-b-0"
                    data-testid={`diff-${d.key}`}
                  >
                    <div className="p-2 font-mono text-[11px]">{label(d.key)}</div>
                    <div className="p-2 border-l border-zinc-200">{fmtValue(d.current)}</div>
                    <div className="p-2 border-l border-zinc-200 bg-emerald-50">{fmtValue(d.proposed)}</div>
                  </div>
                ))}
              </div>
            )}

            {isRejecting ? (
              <div className="space-y-2">
                <textarea
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  rows={2}
                  placeholder="Why are you rejecting this?"
                  data-testid={`reject-notes-${row.id}`}
                  className="w-full border border-black p-2 text-xs font-mono"
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={confirmReject}
                    disabled={busyId === row.id}
                    data-testid={`reject-confirm-${row.id}`}
                    className="bg-rose-600 text-white px-3 py-1.5 text-[11px] uppercase tracking-widest border border-black disabled:opacity-40 inline-flex items-center gap-1"
                  >
                    <XCircle size={12} weight="bold" /> Confirm reject
                  </button>
                  <button
                    onClick={() => setRejectingId(null)}
                    data-testid={`reject-cancel-${row.id}`}
                    className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => approve(row)}
                  disabled={busyId === row.id || diffs.length === 0}
                  data-testid={`approve-${row.id}`}
                  className="bg-emerald-700 text-white px-3 py-1.5 text-[11px] uppercase tracking-widest border border-black disabled:opacity-40 inline-flex items-center gap-1"
                >
                  <CheckCircle size={12} weight="bold" />
                  {busyId === row.id ? "Applying…" : "Approve & apply"}
                </button>
                <button
                  onClick={() => startReject(row)}
                  disabled={busyId === row.id}
                  data-testid={`reject-open-${row.id}`}
                  className="border border-black px-3 py-1.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1"
                >
                  <XCircle size={12} weight="bold" /> Reject
                </button>
              </div>
            )}
          </div>
        );
      })}

      {showResolved && resolved.length > 0 && (
        <div className="border-t-4 border-black">
          <div className="px-3 py-2 label-mono bg-zinc-50 border-b border-black">
            Resolved history
          </div>
          {resolved.map((row) => (
            <div
              key={row.id}
              className="p-3 border-b border-zinc-200 last:border-b-0 text-xs"
              data-testid={`criteria-request-resolved-${row.id}`}
            >
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <StatusChip status={row.status} />
                <b>{row.requester_email}</b>
                <span className="ml-auto text-[10px] font-mono text-zinc-500">
                  {new Date(row.reviewed_at || row.updated_at || row.created_at).toLocaleString()}
                </span>
              </div>
              {row.review_notes && (
                <div className="text-[11px] text-zinc-700 mt-1">
                  Reviewer notes: {row.review_notes}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { diffFields, label as fieldLabel };